/**
 * Due date filter with presets (Before Today, Today, None)
 * and custom date range picker. Parent enforces mutual exclusivity between
 * date ranges and no_due_date filter.
 */

import { useCallback, useMemo } from 'react';

import { CustomDateRange } from '@/components/common/DataTable';

import {
  DUE_DATE_PRESET_IDS,
  getActiveDueDatePreset,
  getPresetDateValues,
} from '../../filterPresets';
import { ChipFilterGroup } from './ChipFilterGroup';

// Preset options using centralized IDs (matches slaResolutionPresets pattern)
const DUE_DATE_PRESETS = [
  { label: 'Before Today', value: DUE_DATE_PRESET_IDS.BEFORE_TODAY },
  { label: 'Today', value: DUE_DATE_PRESET_IDS.TODAY },
  { label: 'None', value: DUE_DATE_PRESET_IDS.NONE },
];

interface DueDateFilterProps {
  /** Start date value (controlled by parent) */
  customRangeStart?: string;
  /** End date value (controlled by parent) */
  customRangeEnd?: string;
  /** No due date boolean value (controlled by parent) */
  noDueDateValue?: boolean;
  /** Callback when date range changes - updates immediately */
  onCustomRangeChange?: (start?: string, end?: string) => void;
  /** Callback when no_due_date changes - updates immediately */
  onNoDueDateChange?: (value: boolean) => void;
}

export const DueDateFilter = ({
  customRangeStart,
  customRangeEnd,
  noDueDateValue = false,
  onCustomRangeChange,
  onNoDueDateChange,
}: DueDateFilterProps) => {
  // Determine which preset is active based on current filter state (using shared utility)
  const activePresets = useMemo(
    () =>
      getActiveDueDatePreset({
        startDate: customRangeStart,
        endDate: customRangeEnd,
        noDueDate: noDueDateValue,
      }),
    [customRangeStart, customRangeEnd, noDueDateValue],
  );

  // Handle preset selection - uses shared utility for date values
  // Parent handlers enforce mutual exclusivity
  const handlePresetChange = useCallback(
    (values: string[]) => {
      // If deselecting (empty array), clear everything
      if (values.length === 0) {
        onCustomRangeChange?.(undefined, undefined);
        return;
      }

      const presetId = values[0];
      const { startDate, endDate, noDueDate } = getPresetDateValues(presetId);

      // Apply preset - parent handlers enforce mutual exclusivity
      if (noDueDate) {
        onNoDueDateChange?.(true);
      } else {
        onCustomRangeChange?.(startDate, endDate);
      }
    },
    [onCustomRangeChange, onNoDueDateChange],
  );

  // Handle start date change - parent enforces mutual exclusivity
  const handleStartChange = useCallback(
    (date: string | undefined) => {
      // Parent handler will auto-clear no_due_date when dates are set
      onCustomRangeChange?.(date, customRangeEnd);
    },
    [customRangeEnd, onCustomRangeChange],
  );

  // Handle end date change - parent enforces mutual exclusivity
  const handleEndChange = useCallback(
    (date: string | undefined) => {
      // Parent handler will auto-clear no_due_date when dates are set
      onCustomRangeChange?.(customRangeStart, date);
    },
    [customRangeStart, onCustomRangeChange],
  );

  // If callbacks are not provided, just render presets
  if (!onCustomRangeChange) {
    return (
      <ChipFilterGroup
        options={DUE_DATE_PRESETS}
        selectedValues={activePresets}
        onChange={handlePresetChange}
        singleSelect
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Preset chips - single select since presets are mutually exclusive */}
      <ChipFilterGroup
        options={DUE_DATE_PRESETS}
        selectedValues={activePresets}
        onChange={handlePresetChange}
        singleSelect
      />

      {/* Divider */}
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <div className="flex-1 border-t border-zinc-200 dark:border-zinc-700" />
        <span>or</span>
        <div className="flex-1 border-t border-zinc-200 dark:border-zinc-700" />
      </div>

      {/* Custom date range - immediate updates, calendar opens below in panel context */}
      <CustomDateRange
        startDate={customRangeStart}
        endDate={customRangeEnd}
        onStartChange={handleStartChange}
        onEndChange={handleEndChange}
        calendarPosition="below"
        showLabel={false}
      />
    </div>
  );
};
